<template>
  <div class="page-section tu-scope uni-page">
    <div :class="containerClass">

      <!-- Hero: same treatment as the public TransitionU school page -->
      <section class="uni-hero" :style="heroStyle" aria-labelledby="uni-heading">
        <div class="uni-hero__inner">
          <div class="uni-cover" :style="coverStyle" aria-hidden="true"></div>
          <div class="uni-hero__top">
            <span class="uni-hero__logo" aria-hidden="true">
              <img v-if="selectedUni.small_logo_url" :src="selectedUni.small_logo_url" :alt="`${selectedUni.name || 'School'} logo`" />
              <span v-else>{{ initial }}</span>
            </span>
            <div>
              <h1 id="uni-heading" class="uni-hero__title">
                <template v-if="isLoading">Loading…</template>
                <template v-else>{{ selectedUni.name || 'School' }}</template>
              </h1>
              <p v-if="locationLabel" class="uni-hero__location"><md-icon>place</md-icon> {{ locationLabel }}</p>
            </div>
          </div>
          <div v-if="bioText" class="uni-hero__bio">
            <p :class="{ 'is-collapsed': !bioExpanded && bioIsLong }">{{ bioText }}</p>
            <button v-if="bioIsLong" type="button" class="uni-hero__more" @click="bioExpanded = !bioExpanded">
              {{ bioExpanded ? 'Read less' : 'Read more' }}
              <md-icon>{{ bioExpanded ? 'expand_less' : 'expand_more' }}</md-icon>
            </button>
          </div>
        </div>
      </section>

      <div class="tu-layout">
        <!-- Summary rail: the facts and the one action -->
        <aside class="tu-rail" aria-label="School summary" v-if="!isLoading">
          <div class="tu-rail-card">
            <dl class="tu-facts">
              <div v-if="locationLabel"><dt>Location</dt><dd>{{ locationLabel }}</dd></div>
              <div v-if="uniPrograms.length"><dt>Programs</dt><dd>{{ uniPrograms.length }}</dd></div>
              <div><dt>Scorecard</dt><dd>{{ checklistAccess.enrolledHere ? 'Unlocked' : 'Locked' }}</dd></div>
            </dl>
            <router-link :to="{ name: 'student-pathways', query: { uni: selectedUni.slug } }" class="tu-btn tu-btn--primary uni-rail-cta">
              <md-icon>directions</md-icon> Explore paths
            </router-link>
            <a v-if="selectedUni.website" :href="selectedUni.website" target="_blank" rel="noopener" class="tu-btn tu-btn--ghost uni-rail-ghost">School website <md-icon>open_in_new</md-icon></a>
          </div>
        </aside>

        <div class="tu-main uni-tabpanels">
        <tu-section-tabs :items="sectionTabs" />

        <!-- OVERVIEW -->
        <section id="uni-overview" class="uni-tabpanel">
          <header class="uni-tab-intro">
            <h2 class="uni-tab-intro__title">At a glance</h2>
          </header>
          <div class="uni-stats" aria-label="At a glance">
            <div class="uni-stat"><strong>{{ uniPrograms.length }}</strong><span>Healthcare programs</span></div>
            <div class="uni-stat"><strong>{{ uniPathways.length }}</strong><span>Paths to this school</span></div>
          </div>
          <template v-if="offeringLabels.length">
            <h3 class="uni-offerings-title">Offerings</h3>
            <ul class="uni-offerings">
              <li v-for="label in offeringLabels" :key="label"><md-icon>check</md-icon><span>{{ label }}</span></li>
            </ul>
          </template>
        </section>

        <!-- PROGRAMS -->
        <section id="uni-programs" class="uni-tabpanel">
          <header class="uni-tab-intro">
            <h2 class="uni-tab-intro__title">Programs</h2>
            <p class="uni-tab-intro__lead">Compare tuition, requirements and application dates.</p>
          </header>
          <div v-if="programsLoading" class="tu-card-grid" aria-busy="true">
            <div v-for="i in 2" :key="`pg-${i}`" class="tu-skel"></div>
          </div>
          <div v-else-if="uniPrograms.length" class="tu-card-grid uni-program-grid">
            <uni-program-card
              v-for="offering in uniPrograms"
              :key="offering.id"
              :offering="offering"
              :school="selectedUni"
              :to="{ name: 'student-program-detail', params: { uniSlug: selectedUni.slug, programSlug: get(offering, 'program.slug') } }"
            />
          </div>
          <div v-else class="tu-empty">
            <md-icon>menu_book</md-icon>
            <h4 class="tu-empty-title">No programs to show yet</h4>
            <p class="tu-empty-text">Program details aren’t listed yet. Check the school’s website for current offerings.</p>
          </div>
        </section>

        <!-- PATHS -->
        <section id="uni-paths" class="uni-tabpanel">
          <header class="uni-tab-intro">
            <h2 class="uni-tab-intro__title">Paths to this school</h2>
            <p class="uni-tab-intro__lead">Explore starting credentials and the steps toward these programs.</p>
          </header>
          <div v-if="pathsLoading" class="tu-card-grid" aria-busy="true">
            <div v-for="i in 2" :key="`pw-${i}`" class="tu-skel"></div>
          </div>
          <div v-else-if="uniPathways.length" class="tu-card-grid uni-path-grid">
            <pathway-card v-for="pw in uniPathways" :key="pw.id" :pathway="pw" :enrolled="enrolledPathwayIds.has(pw.id)" @view="viewPathway" />
          </div>
          <div v-else class="tu-empty">
            <md-icon>directions</md-icon>
            <h4 class="tu-empty-title">No mapped paths yet</h4>
            <p class="tu-empty-text">This school's programs haven't been mapped to a path yet.</p>
            <router-link :to="{ name: 'student-pathways' }" class="tu-btn tu-btn--ghost tu-empty-cta">Browse paths</router-link>
          </div>
        </section>

        <!-- CHECKLIST (unlocks with a pathway enrollment that includes this school) -->
        <section v-if="isLoading || checklistAccess.enrolledHere" id="uni-checklist" class="uni-tabpanel">
          <header class="uni-tab-intro">
            <h2 class="uni-tab-intro__title">Your scorecard at {{ selectedUni.name || 'this school' }}</h2>
            <p class="uni-tab-intro__lead">
              Each program scores your application. Complete the steps inside to raise your score.
            </p>
          </header>

          <div v-if="isLoading" class="tu-card-grid" aria-busy="true">
            <div v-for="i in 3" :key="`cs-${i}`" class="tu-skel tu-skel--scope"></div>
          </div>

          <template v-else>
            <div class="tu-card-grid uni-scope-grid">
              <a
                v-for="scopeCard in checklistScopeCards"
                :key="scopeCard.key"
                href="#"
                class="uni-scope-card tu-browse-card"
                @click.prevent="openChecklistScope(scopeCard)"
              >
                <div class="uni-scope-card__head">
                  <h3 class="uni-scope-card__title">{{ scopeCard.title }}</h3>
                  <span class="uni-scope-card__points"><md-icon>emoji_events</md-icon>{{ $n(scopeCard.earnedPoints || 0) }} pts</span>
                </div>
                <div class="uni-scope-card__progress">
                  <div class="uni-scope-card__progress-row">
                    <span>Progress</span>
                    <span>{{ getChecklistProgress(scopeCard).completedChecks }}/{{ getChecklistProgress(scopeCard).totalEnabledChecks }}</span>
                  </div>
                  <div class="uni-scope-card__bar" role="progressbar" :aria-valuenow="getChecklistProgress(scopeCard).completionPercent" aria-valuemin="0" aria-valuemax="100">
                    <span :style="{ width: `${getChecklistProgress(scopeCard).completionPercent}%` }"></span>
                  </div>
                  <div class="uni-scope-card__pct">{{ getChecklistProgress(scopeCard).completionPercent }}% completed</div>
                </div>
                <span class="tu-card-action">
                  <span class="tu-card-action-label">View scorecard</span>
                  <md-icon>chevron_right</md-icon>
                </span>
              </a>
            </div>
          </template>

        </section>

          <div class="uni-panel-card uni-connect" v-if="!isLoading">
            <div>
              <h3>Connect with admissions</h3>
              <p>
                Interested in healthcare programs, scholarships, or admissions at {{ selectedUni.name || 'this school' }}?
                Request personalized information directly from the admissions team.
              </p>
            </div>
            <button v-if="studentContactStatus.is_contacted" type="button" class="tu-btn tu-btn--ghost" disabled>
              <md-icon>check</md-icon> Request sent
            </button>
            <button v-else type="button" class="tu-btn tu-btn--primary" @click="contactAction">Connect me</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, isEmpty, truncate } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';
import TuSectionTabs from '@/components/Ui/TuSectionTabs.vue';
import UniProgramCard from './UniProgramCard.vue';
import PathwayCard from '../Pathways/PathwayCard.vue';
import { DASHBOARD_TYPES, UNI_OFFERINGS } from '@/common/constants';
import { enrollmentAccessForUniversity, stateAbbr } from '@/common/tuPathway';

// Shown in the locked preview when a school has no general checklist configured yet.
const DEFAULT_PREVIEW_STEPS = [
  { title: 'Take a Career Assessment', points: 5 },
  { title: 'Add Education to Resume', points: 1 },
  { title: 'Add Work Experience to Resume', points: 1 },
  { title: 'Add Skills to Resume', points: 1 },
  { title: 'Interview', points: 3 },
  { title: 'Add Certifications', points: 5 },
  { title: 'Add Courses', points: 3 },
];


/**
 * Student portal school page. Mirrors the public TransitionU school page
 * (hero, key facts, Programs / Paths / Overview tabs). The one portal-only
 * section is the Checklist tab, which stays locked until the student holds a
 * pathway enrollment that includes this school; program checklists then show
 * only for the (school, program) pairs on that enrollment.
 */
export default {
  extends: Page,
  name: 'SchoolSetupGuide',
  components: { TuSectionTabs, UniProgramCard, PathwayCard },
  data() {
    return {
      title: 'School',
      completeHeadTitle: 'School - Transition',
      studentContactStatus: { is_contacted: false },
      isLoading: true,
      windowWidth: window.innerWidth,
      DASHBOARD_TYPES,
      bioExpanded: false,
      selectedUni: {},
      careCampusUrl: process.env.VUE_APP_UNI_SPONSORED_URL,
      checklistScoreMap: {},
      checklistProgressMap: {},
      // Pathway enrollments decide which checklists this student may open here.
      myEnrollments: [],
      uniPrograms: [],
      programsLoading: true,
      uniPathways: [],
      pathsLoading: true,
    };
  },
  watch: {
    selectedUni: {
      handler(value) {
        if (get(value, 'id')) {
          this.fetchStudentUniContactStatus();
        }
      },
    },
  },

  computed: {
    sectionTabs() {
      const tabs = [
        { id: 'uni-overview', label: 'Overview', icon: 'info' },
        { id: 'uni-programs', label: 'Programs', icon: 'menu_book' },
        { id: 'uni-paths', label: 'Paths', icon: 'directions' },
      ];
      if (this.checklistAccess.enrolledHere) tabs.push({ id: 'uni-checklist', label: 'Scorecard', icon: 'assignment_turned_in' });
      return tabs;
    },
    offeringLabels() {
      const byValue = {};
      UNI_OFFERINGS.forEach(g => g.options.forEach(o => { byValue[o.value] = o.text; }));
      const list = Array.isArray(this.selectedUni.offering) ? this.selectedUni.offering : [];
      return list.map(v => (v === 'other' ? this.selectedUni.other_offering : byValue[v] || v)).filter(Boolean);
    },
    enrolledPathwayIds() {
      return new Set((this.myEnrollments || []).map(e => get(e, 'pathway.id') || get(e, 'pathway')).filter(Boolean));
    },
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    ...mapGetters('uni', ['getSelectedStudentUniversity']),

    isMobile() {
      return this.windowWidth <= 768;
    },
    initial() {
      return String(this.selectedUni.name || '?').trim().charAt(0).toUpperCase();
    },
    heroStyle() {
      return {};
    },
    coverStyle() {
      const bg = String(this.selectedUni.campus_img_url || '').trim();
      const brand = String(this.selectedUni.primary_color || '').trim();
      if (bg) return { backgroundImage: `url(${bg})` };
      return { background: brand && !['#fff', '#ffffff', 'white'].includes(brand.toLowerCase()) ? brand : '#0f2a5f' };
    },
    // Preserve authored bios; shorten the repetitive directory-generated introduction.
    bioText() {
      const short = String(this.selectedUni.short_bio || '').trim();
      const long = String(this.selectedUni.bio || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      const text = short || long;
      const generated = text.match(/^.+? is an? (?:university|college|school|institution) in .+? offering (.+?)\.?$/i);
      const summary = generated ? `Explore ${generated[1].replace(/\.$/, '')}.` : text;
      return summary.length > 220 ? `${summary.slice(0, 217).trim()}…` : summary;
    },
    bioIsLong() {
      const len = this.bioText.length;
      return this.isMobile ? len > 200 : len > 400;
    },
    checklistAccess() {
      return enrollmentAccessForUniversity(this.myEnrollments, get(this.selectedUni, 'id'));
    },
    locationLabel() {
      return [this.selectedUni.city, stateAbbr(this.selectedUni.state)].filter(Boolean).join(', ');
    },
    programCount() {
      const list = this.selectedUni.program_badges || this.selectedUni.offered_programs || [];
      return Array.isArray(list) ? list.length : 0;
    },
    passRateLabel() {
      const n = Number(this.selectedUni.pass_rate);
      return Number.isFinite(n) && n > 0 ? `${this.$n(n)}%` : '';
    },
    tuitionLabel() {
      const fn = Number(this.selectedUni.tuition_from);
      const tn = Number(this.selectedUni.tuition_to);
      const hasFrom = Number.isFinite(fn) && fn > 0;
      const hasTo = Number.isFinite(tn) && tn > 0;
      if (!hasFrom && !hasTo) return '';
      if (hasFrom && hasTo) return fn === tn ? `$${this.$n(fn)}` : `$${this.$n(fn)} to $${this.$n(tn)}`;
      return hasFrom ? `From $${this.$n(fn)}` : `Up to $${this.$n(tn)}`;
    },
    generalChecklistCount() {
      const checklist = get(this.selectedUni, 'general_checklist', []);
      if (Array.isArray(checklist) && checklist.length) {
        return checklist.filter(item => item && item.is_enabled !== false).length;
      }
      return 9;
    },
    programChecklistCards() {
      const groups = get(this.selectedUni, 'program_checklists', []);
      if (!Array.isArray(groups) || !groups.length) {
        return [];
      }
      return groups
        .map(group => {
          const items = (group.checklist || []).filter(item => item && item.is_enabled !== false);
          return {
            key: `program-${group.program_id}`,
            title: group.program_title || 'Program scorecard',
            scope: 'program',
            programId: Number(group.program_id) || null,
            itemsCount: items.length,
          };
        })
        .filter(card => card.itemsCount);
    },
    // Locked preview: every checklist this school offers, with step titles and total points.
    previewScopeCards() {
      const normalize = (list = []) =>
        (list || [])
          .filter(item => item && item.is_enabled !== false)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(item => ({ title: item.title, points: Number(item.points) || 0 }));
      const cards = [];
      const general = normalize(get(this.selectedUni, 'general_checklist', []));
      const generalItems = general.length ? general : DEFAULT_PREVIEW_STEPS;
      cards.push({ key: 'general', title: 'General Program', items: generalItems, totalPoints: generalItems.reduce((n, i) => n + i.points, 0) });
      (get(this.selectedUni, 'program_checklists', []) || []).forEach(group => {
        const items = normalize(group.checklist);
        if (!items.length) return;
        cards.push({
          key: `program-${group.program_id}`,
          title: group.program_title || 'Program scorecard',
          items,
          totalPoints: items.reduce((n, i) => n + i.points, 0),
        });
      });
      return cards;
    },
    checklistScopeCards() {
      const generalCard = {
        key: 'general',
        title: 'General Program',
        scope: 'general',
        programId: null,
        itemsCount: this.generalChecklistCount,
        earnedPoints: Number(this.checklistScoreMap.general) || 0,
      };
      // Only programs the student's pathway enrollment ties to this school.
      const programCards = this.programChecklistCards
        .filter(card => card.programId && this.checklistAccess.programIds.has(card.programId))
        .map(card => ({
          ...card,
          earnedPoints: Number(this.checklistScoreMap[card.key]) || 0,
        }));
      return [generalCard, ...programCards];
    },
  },

  methods: {
    ...mapActions('student', [
      'createStudentUniversityConnection',
      'getStudentUniConnectionStatus',
      'getBulkUniversityChecklistScore',
    ]),
    ...mapActions('uni', ['getUniFromSlug', 'getAllUniversities']),
    ...mapActions('pathways', ['getMyPathwayEnrollments', 'getAllPathways']),
    ...mapActions('program', ['getPublicUniOnlyPrograms']),
    get,
    isEmpty,

    goBackToSchools() {
      this.$router.push({ name: 'student-institutions' });
    },

    openChecklistScope(scopeCard) {
      const query = {
        scope: scopeCard.scope,
        ...(scopeCard.scope === 'program' && scopeCard.programId ? { program_id: scopeCard.programId } : {}),
      };
      this.$router.push({
        name: 'std-cc-uni-checklist-detail',
        params: { slug: this.$route.params.slug },
        query,
      });
    },

    mergeChecklistConfig(university = {}, configuredUniversity = {}) {
      const baseUniversity = university && Object.keys(university).length ? university : configuredUniversity || {};
      if (!configuredUniversity || !Object.keys(configuredUniversity).length) {
        return baseUniversity;
      }
      return {
        ...configuredUniversity,
        ...baseUniversity,
        ...(configuredUniversity.general_checklist && {
          general_checklist: configuredUniversity.general_checklist,
        }),
        ...(configuredUniversity.program_checklists && {
          program_checklists: configuredUniversity.program_checklists,
        }),
      };
    },

    async fetchChecklistConfigBySlug(slug) {
      try {
        const response = await this.getAllUniversities({ slug, limit: 1, offset: 0 });
        const configuredUniversity = get(response, 'data.results.0', null);
        if (configuredUniversity) {
          this.selectedUni = this.mergeChecklistConfig(this.selectedUni, configuredUniversity);
        }
      } catch (_err) {
        return null;
      }
    },

    async fetchMyEnrollments() {
      try {
        const response = await this.getMyPathwayEnrollments({ limit: 200 });
        this.myEnrollments = get(response, 'data.results', []) || [];
      } catch (_err) {
        this.myEnrollments = [];
      }
    },

    buildBulkChecklistScorePayload() {
      const universityId = Number(get(this.selectedUni, 'id'));
      if (!universityId) {
        return [];
      }
      return this.checklistScopeCards.map(card => ({
        university_id: universityId,
        program_id: card.scope === 'program' && card.programId ? card.programId : null,
      }));
    },
    getChecklistProgress(scopeCard) {
      const progress = this.checklistProgressMap[scopeCard.key] || {};
      const totalEnabledChecks = Number(progress.total_enabled_checks);
      const completedChecks = Number(progress.completed_checks);
      const completionPercent = Number(progress.completion_percent);
      const safeTotal = Number.isFinite(totalEnabledChecks) && totalEnabledChecks >= 0 ? totalEnabledChecks : scopeCard.itemsCount || 0;
      const safeCompleted = Number.isFinite(completedChecks) && completedChecks >= 0 ? completedChecks : 0;
      const percentFromCounts = safeTotal > 0 ? Math.round((safeCompleted / safeTotal) * 100) : 0;
      const safePercent = Number.isFinite(completionPercent)
        ? Math.max(0, Math.min(100, Math.round(completionPercent)))
        : percentFromCounts;
      return { completedChecks: safeCompleted, totalEnabledChecks: safeTotal, completionPercent: safePercent };
    },

    async fetchBulkChecklistScores() {
      const payload = this.buildBulkChecklistScorePayload();
      if (!payload.length) {
        this.checklistScoreMap = {};
        this.checklistProgressMap = {};
        return;
      }
      try {
        const response = await this.getBulkUniversityChecklistScore(payload);
        const results = get(response, 'data.data.results') || get(response, 'data.results') || [];
        const scoreMap = {};
        const progressMap = {};
        results.forEach(item => {
          if (!item) return;
          const programId = Number(item.program_id);
          const cardKey = Number.isFinite(programId) && programId > 0 ? `program-${programId}` : 'general';
          scoreMap[cardKey] = Number(item.earned_points_sum) || 0;
          progressMap[cardKey] = get(item, 'checklist_progress', {});
        });
        this.checklistScoreMap = scoreMap;
        this.checklistProgressMap = progressMap;
      } catch (_err) {
        this.checklistScoreMap = {};
        this.checklistProgressMap = {};
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    async fetchStudentUniContactStatus() {
      if (!get(this.selectedUni, 'id')) {
        this.studentContactStatus = { is_contacted: false };
        return;
      }
      try {
        const resp = await this.getStudentUniConnectionStatus({ university: this.selectedUni.id });
        this.studentContactStatus = resp.data;
      } catch (_err) {
        this.studentContactStatus = { is_contacted: false };
      }
    },
    async contactAction() {
      if (!get(this.selectedUni, 'id')) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        return;
      }
      await this.createStudentUniversityConnection({ from_entity_id: this.selectedUni.id });
      this.fetchStudentUniContactStatus();
    },
    viewPathway(pathway) {
      this.$router.push({ name: 'student-pathway-detail', params: { id: pathway.id } });
    },
    async fetchUniPrograms(slug) {
      this.programsLoading = true;
      try {
        const response = await this.getPublicUniOnlyPrograms({ slug });
        const list = get(response, 'data.data', get(response, 'data', [])) || [];
        this.uniPrograms = (Array.isArray(list) ? list : []).filter(o => o && o.program);
      } catch (err) {
        this.uniPrograms = [];
      }
      this.programsLoading = false;
    },
    async fetchUniPathways(slug) {
      this.pathsLoading = true;
      try {
        const response = await this.getAllPathways({ limit: 50, uni_offered_programs__university__slug: slug });
        const list = get(response, 'data.results', get(response, 'data', [])) || [];
        this.uniPathways = Array.isArray(list) ? list : [];
      } catch (err) {
        this.uniPathways = [];
      }
      this.pathsLoading = false;
    },
    async fetchUniversity() {
      this.isLoading = true;
      const uniSlug = this.$route.params.slug;
      if (uniSlug) {
        const response = await this.getUniFromSlug(uniSlug);
        let university =
          get(response, 'data.data') ||
          get(response, 'data') ||
          get(response, 'results.0') ||
          get(response, 'data.results.0') ||
          get(response, 'university') ||
          response ||
          {};
        if (Array.isArray(university)) {
          university = university[0] || {};
        }
        this.selectedUni = this.mergeChecklistConfig(
          university,
          get(this.getSelectedStudentUniversity, 'slug') === uniSlug ? this.getSelectedStudentUniversity : null,
        );
        await Promise.all([this.fetchChecklistConfigBySlug(uniSlug), this.fetchMyEnrollments()]);
        if (this.checklistAccess.enrolledHere) {
          await this.fetchBulkChecklistScores();
        }
        this.completeHeadTitle = `${truncate(this.selectedUni.name)} - Transition`;
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.fetchUniversity();
    if (this.$route.params.slug) {
      this.fetchUniPrograms(this.$route.params.slug);
      this.fetchUniPathways(this.$route.params.slug);
    }
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
.uni-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  padding: 6px 0;
  margin-bottom: 14px;
  color: var(--tu-ink-mute);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  .md-icon { font-size: 18px; }
  &:hover { color: var(--tu-ink); }
}

/* ---------- Hero ---------- */
.uni-hero {
  position: relative;
  min-height: 0;
  display: flex;
  align-items: center;
  color: var(--tu-ink);
}
.uni-hero__inner {
  width: 100%;
  padding: 24px 0 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.uni-hero__top {
  display: flex;
  align-items: center;
  gap: 16px;
}
.uni-hero__logo {
  flex: none;
  width: 85px;
  height: 85px;
  padding: 8px;
  border-radius: 12px;
  background: var(--tu-surface);
  border: 1px solid var(--tu-line);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 800;
  color: var(--tu-ink-mute);
  img { max-width: 100%; max-height: 100%; object-fit: contain; }
}
.uni-hero__title {
  margin: 0;
  font-size: clamp(30px, 3.6vw, 40px);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: var(--tu-ink);
}
.uni-hero__location {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--tu-ink-mute);
  .md-icon { font-size: 16px; color: inherit; }
}
.uni-hero__bio {
  max-width: 42rem;
  p {
    margin: 0;
    font-size: 1.05rem;
    line-height: 1.6;
    color: var(--tu-ink-soft);
    &.is-collapsed {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}
.uni-hero__more {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--tu-ink);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  .md-icon { font-size: 18px; color: inherit; }
}
/* ---------- Key facts ---------- */
.uni-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 24px 0 28px;
  border: 1px solid var(--tu-line);
  border-radius: var(--tu-r-md);
  overflow: hidden;
  background: var(--tu-surface);
}
.uni-metric {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  min-width: 0;
  border-right: 1px solid var(--tu-line);
  &:last-child { border-right: 0; }
}
.uni-metric-ico {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--tu-surface-2);
  color: var(--tu-ink-soft);
  border: 1px solid var(--tu-line-soft);
  .md-icon { font-size: 20px; color: inherit; }
}
.uni-metric-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.uni-metric-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--tu-ink-mute);
}
.uni-metric-value {
  font-size: 21px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--tu-ink);
}
.uni-metric-value--sm { font-size: 15px; letter-spacing: -0.01em; }
@media (max-width: 860px) {
  .uni-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .uni-metric:nth-child(2n) { border-right: 0; }
  .uni-metric:nth-child(-n + 2) { border-bottom: 1px solid var(--tu-line); }
}
@media (max-width: 420px) {
  .uni-metrics { grid-template-columns: 1fr; }
  .uni-metric { border-right: 0; border-bottom: 1px solid var(--tu-line); }
  .uni-metric:last-child { border-bottom: 0; }
}

/* ---------- Tabs ---------- */
.uni-tabbar {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: var(--tu-surface-2);
  border: 1px solid var(--tu-line-soft);
  overflow-x: auto;
  scrollbar-width: none;
  max-width: 100%;
  &::-webkit-scrollbar { display: none; }
}
.uni-tabpill {
  appearance: none;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  color: var(--tu-ink-soft);
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  .md-icon { font-size: 18px; color: inherit; }
  &:hover { color: var(--tu-ink); }
  &.is-active {
    background: var(--tu-blue);
    color: #fff;
    box-shadow: var(--tu-shadow-card);
  }
}
@media (max-width: 560px) {
  .uni-tabbar { display: flex; width: 100%; }
  .uni-tabpill { flex: 1; padding: 0 10px; font-size: 13px; justify-content: center; }
}
.uni-tabpanels { margin-top: 0; }
.uni-facts-tuition { font-size: 18px; }
.uni-rail-link { display: block; margin-top: 12px; text-align: center; font-size: 13.5px; font-weight: 600; color: var(--tu-blue) !important; text-decoration: none !important; }
.uni-tabpanel { animation: uni-fade 0.32s cubic-bezier(0.22, 1, 0.36, 1); }
@keyframes uni-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .uni-tabpanel { animation: none; } }

.uni-tab-intro { margin-bottom: 24px; }
.uni-tab-intro__title {
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.02em;
  margin: 0 0 10px;
  color: var(--tu-ink);
}
.uni-tab-intro__lead {
  font-size: 16px;
  line-height: 1.6;
  color: var(--tu-ink-soft);
  max-width: 640px;
  margin: 0;
}
.uni-all-paths {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  font-weight: 700;
  color: var(--tu-blue) !important;
  text-decoration: none !important;
  .md-icon { font-size: 18px; color: inherit; }
}

/* ---------- Panels ---------- */
.uni-panel-card {
  background: var(--tu-surface);
  border: 1px solid var(--tu-line);
  border-radius: 12px;
  padding: 20px 22px;
  & + & { margin-top: 16px; }
  h3 { margin: 0 0 12px; font-size: 16px; font-weight: 800; color: var(--tu-ink); }
}
.uni-about {
  font-size: 15px;
  line-height: 1.7;
  color: var(--tu-ink-soft);
  ::v-deep p:last-child { margin-bottom: 0; }
}
.uni-offerings ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 20px;
  li { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--tu-ink); }
  .md-icon { font-size: 18px; color: var(--tu-blue); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
}
.uni-connect {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 24px;
  p { margin: 0; font-size: 14px; line-height: 1.55; color: var(--tu-ink-soft); max-width: 60ch; }
  @media (max-width: 720px) { flex-direction: column; align-items: stretch; }
}

/* ---------- Checklist teaser (locked) ---------- */
.uni-teaser__head {
  text-align: center;
  max-width: 640px;
  margin: 0 auto 24px;
}
.uni-teaser__kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--tu-blue-tint);
  color: var(--tu-blue-deep);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 14px;
  .md-icon { font-size: 14px; color: inherit; }
}
.uni-teaser__title {
  margin: 0 0 10px;
  font-size: clamp(22px, 2.6vw, 30px);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--tu-ink);
}
.uni-teaser__lead {
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.6;
  color: var(--tu-ink-soft);
}
.uni-scope-card--locked {
  border-style: dashed;
  cursor: default;
}
.uni-scope-card__meta { margin: -6px 0 0; font-size: 12.5px; color: var(--tu-ink-mute); }
.uni-teaser__steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  li { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 600; color: var(--tu-ink-soft); }
  li .md-icon { font-size: 16px; color: var(--tu-ink-hint); flex: none; }
  li:nth-child(3) { opacity: 0.75; }
  li:nth-child(4) { opacity: 0.5; }
}
.uni-teaser__more { color: var(--tu-blue) !important; font-size: 13px !important; padding-left: 24px; }
.checklist-locked { padding: 48px 20px; }
.tu-skel--scope { min-height: 170px; }
.uni-scope-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: 14px;
  background: var(--tu-surface);
  border: 1px solid var(--tu-card-border);
  box-shadow: none;
  color: var(--tu-ink) !important;
  text-decoration: none !important;
}
.uni-scope-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.uni-scope-card__title {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--tu-ink);
}
.uni-scope-card__points {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--tu-blue-tint);
  color: var(--tu-blue-deep);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  .md-icon { font-size: 14px; color: inherit; }
}
.uni-scope-card__progress-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: var(--tu-ink-soft);
  margin-bottom: 6px;
}
.uni-scope-card__bar {
  height: 8px;
  border-radius: 999px;
  background: var(--tu-surface-2);
  overflow: hidden;
  span { display: block; height: 100%; border-radius: 999px; background: var(--tu-blue); transition: width 0.3s ease; }
}
.uni-scope-card__pct {
  margin-top: 6px;
  font-size: 12px;
  color: var(--tu-ink-mute);
}

.uni-about { max-width: 68ch; margin: 0; font-size: 15.5px; line-height: 1.65; color: var(--tu-ink-soft); }
.uni-offerings-title { margin: 22px 0 10px; font-size: 15px; font-weight: 700; color: var(--tu-ink); }
.uni-offerings { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; max-width: 68ch; }
.uni-offerings li { display: flex; align-items: flex-start; gap: 8px; font-size: 14px; line-height: 1.5; color: var(--tu-ink); .md-icon { flex: none; font-size: 16px; margin: 2px 0 0; color: var(--tu-blue-deep); } }
.uni-program-grid, .uni-path-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.uni-tabpanels .uni-tabpanel + .uni-tabpanel { margin-top: 32px; padding-top: 26px; border-top: 1px solid var(--tu-line-soft); }
.uni-tabpanels .tu-sectiontabs { margin-top: 0; }


.uni-cover { width: 100%; height: clamp(160px, 22vw, 260px); border-radius: 18px; background-size: cover; background-position: center 35%; background-repeat: no-repeat; background-color: var(--tu-surface-2); }
.uni-hero__inner .uni-hero__top { margin-top: -40px; position: relative; z-index: 1; padding-left: 8px; align-items: flex-start; gap: 18px; }
.uni-hero__inner .uni-hero__top > div { padding-top: 48px; }
.uni-hero__inner .uni-hero__logo { width: 104px; height: 104px; border: 4px solid #fff; border-radius: 16px; box-shadow: 0 6px 18px rgba(24, 24, 24, 0.12); }
.uni-hero__inner .uni-hero__top > div { padding-bottom: 6px; }
.uni-rail-cta { width: 100%; margin-top: 14px; }
.uni-rail-ghost { width: 100%; margin-top: 10px; }
.uni-stats { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin: 0 0 22px; }
.uni-stat { padding: 16px 18px; border: 1px solid rgba(24, 24, 24, 0.1); border-radius: 14px; background: var(--tu-surface); display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.uni-stat strong { font-size: 22px; font-weight: 700; line-height: 1.1; color: var(--tu-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.uni-stat span { font-size: 12.5px; font-weight: 600; color: var(--tu-ink-mute); }
@media (max-width: 760px) { .uni-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
